import { Instagram, Facebook, Youtube, Twitter, Linkedin } from "lucide-react";
import { useSocialMediaLinks } from "@/hooks/useSocialMediaLinks";
import { useLogoSettings } from "@/hooks/useLogoSettings";
import { useLanguage } from "@/hooks/useLanguage";
import { openExternalSocialUrl, sanitizeSocialUrl } from "@/lib/socialMedia";
import AddToHomeScreen from "@/components/AddToHomeScreen";

// Custom icons for platforms not in Lucide
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const PinterestIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
  </svg>
);

const SnapchatIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03c-.135 0-.313-.031-.538-.074-.36-.075-.765-.135-1.273-.135-.3 0-.599.015-.913.074-.6.104-1.123.464-1.723.884-.853.599-1.826 1.288-3.294 1.288-.06 0-.119-.015-.18-.015h-.149c-1.468 0-2.427-.675-3.279-1.288-.599-.42-1.107-.779-1.707-.884-.314-.045-.629-.074-.928-.074-.54 0-.958.089-1.272.149-.211.043-.391.074-.54.074-.374 0-.523-.224-.583-.42-.061-.192-.09-.389-.135-.567-.046-.181-.105-.494-.166-.57-1.918-.222-2.95-.642-3.189-1.226-.031-.063-.052-.15-.055-.225-.015-.243.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.434-.884-.658-1.332-.809-.121-.029-.24-.074-.346-.119-.809-.329-1.24-.72-1.227-1.153 0-.359.285-.69.735-.838.149-.06.314-.09.494-.09.12 0 .3.015.465.104.382.18.735.301 1.049.301.181 0 .314-.045.399-.09l-.029-.507c-.104-1.63-.239-3.654.286-4.847C7.859 1.069 11.216.793 12.206.793"/>
  </svg>
);

const platformIcons: Record<string, React.FC<{ className?: string }>> = {
  instagram: Instagram,
  facebook: Facebook,
  tiktok: TikTokIcon,
  youtube: Youtube,
  twitter: Twitter,
  linkedin: Linkedin,
  pinterest: PinterestIcon,
  snapchat: SnapchatIcon,
};

const platformColors: Record<string, string> = {
  instagram: "text-pink-500",
  facebook: "text-blue-600",
  tiktok: "text-black",
  youtube: "text-red-600",
  twitter: "text-sky-500",
  linkedin: "text-blue-700",
  pinterest: "text-red-700",
  snapchat: "text-yellow-400",
};

const Footer = () => {
  const { data: socialLinks, isLoading } = useSocialMediaLinks();
  const { logoSettings } = useLogoSettings();
  const { t } = useLanguage();
  
  const logoText = logoSettings?.logo_text || "ANAYA";
  const activeSocialLinks = socialLinks?.filter(link => link.url && link.url.trim() !== "") || [];

  return (
    <footer className="py-12 bg-strip-beige border-t border-brown/20" style={{ minHeight: '217px', contain: 'layout' }}>
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center justify-center gap-6">
          
          <div className="flex items-center gap-4" style={{ minHeight: '40px' }}>
            {!isLoading && activeSocialLinks.map((link) => {
              const Icon = platformIcons[link.platform] || Instagram;
              const colorClass = platformColors[link.platform] || "text-muted-foreground";
              const socialUrl = sanitizeSocialUrl(link.platform, link.url);

              if (!socialUrl) return null;

              return (
                <a 
                  key={link.id}
                  href={socialUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  referrerPolicy="no-referrer"
                  onClick={(event) => openExternalSocialUrl(event, socialUrl)}
                  className={`w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center ${colorClass} hover:opacity-80 hover:bg-gold/20 transition-all duration-300`}
                  aria-label={link.platform}
                >
                  <Icon className="h-5 w-5" />
                </a>
              );
            })}
          </div>
          
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6" style={{ minHeight: '56px' }}>
            <p className="text-muted-foreground text-sm leading-5">
              © 2024 {logoText} Perfume. {t("footer.allRightsReserved")}.
            </p>
            <div className="flex gap-6">
              <a href="/privacy-policy" className="text-muted-foreground hover:text-gold transition-colors text-sm leading-5">
                {t("nav.privacyPolicy")}
              </a>
              <a href="/terms-conditions" className="text-muted-foreground hover:text-gold transition-colors text-sm leading-5">
                {t("nav.termsConditions")}
              </a>
              <a href="/contact" className="text-muted-foreground hover:text-gold transition-colors text-sm leading-5">
                {t("nav.contact")}
              </a>
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-center">
          <AddToHomeScreen />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
